// The real wiring of the autosave controller: the calculator store, the sync store, the throwing
// `apiFetch`, timers, and the pagehide keepalive flush. One controller for the page's lifetime
// (module scope), so its baseline survives the shell unmounting for the deck route and remounting.

import { useEffect } from 'react';
import { useApi } from '../../_shared/hooks';
import {
  impersonateWriteHeaders,
  isImpersonating,
  useImpersonateWriteModeStore,
} from '../../_shared/hooks/impersonate-write-mode';
import { saveSnapshot } from '../api-client';
import { flushBlockedByImpersonation, keepaliveFlushHeaders } from '../components/calculator/autosave';
import { loadBenchmarkRows } from './benchmark-store';
import { createAutosave, type Autosave } from './autosave';
import { useSyncStore } from './autosave-store';
import { sizingOf, useCalculatorStore } from './calculator-store';
import { buildSaveRequest } from './save-request';

type ApiFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** Kept current by `useAutosave`; the controller calls through it. */
let latestApiFetch: ApiFetch | null = null;

/** Browsers cap in-flight keepalive bodies at 64KB; warn well before real stories approach it. */
const KEEPALIVE_WARN_BYTES = 60_000;

let shared: Autosave | null = null;

function controller(): Autosave {
  shared ??= createAutosave({
    getState: () => useCalculatorStore.getState(),
    subscribe: (listener) => useCalculatorStore.subscribe(listener),
    buildBody: (account, state) =>
      buildSaveRequest(account, sizingOf(state), { created: Date.now(), benchmarkRows: loadBenchmarkRows() }),
    push: async (body) => {
      if (!latestApiFetch) throw new Error('apiFetch is not ready');
      await saveSnapshot(latestApiFetch, body);
    },
    pushKeepalive: async (body) => {
      const payload = JSON.stringify(body);
      if (payload.length > KEEPALIVE_WARN_BYTES) {
        console.warn(`[value-calculator] pagehide flush payload is ${payload.length}B, near the 64KB keepalive cap`);
      }
      const res = await fetch('/api/tools/value-calculator/snapshots', {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        headers: keepaliveFlushHeaders(impersonateWriteHeaders()),
        body: payload,
      });
      return res.ok;
    },
    flushAllowed: () =>
      !flushBlockedByImpersonation({
        impersonating: isImpersonating(),
        writesUnlocked: useImpersonateWriteModeStore.getState().enabled,
      }),
    now: () => Date.now(),
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
    setSync: (patch) => useSyncStore.setState(patch),
    getSync: () => useSyncStore.getState(),
  });
  return shared;
}

/** Force a push now (the sync chip). A no-op until `useAutosave` has mounted once. */
export function forceSync(): void {
  shared?.forceSync();
}

/**
 * Runs autosave while the guided shell is mounted. Unmounting (navigating to the deck route)
 * flushes unsynced edits; `pagehide` covers a hard reload or tab close with a keepalive POST.
 */
export function useAutosave(): void {
  const { apiFetch } = useApi();
  useEffect(() => {
    latestApiFetch = apiFetch;
  }, [apiFetch]);

  useEffect(() => {
    const autosave = controller();
    autosave.start();
    const onPagehide = () => autosave.flushPending({ keepalive: true });
    window.addEventListener('pagehide', onPagehide);
    return () => {
      window.removeEventListener('pagehide', onPagehide);
      autosave.stop();
    };
  }, []);
}
